import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, Save, Plus, Trash2, Shield, Download, ArrowLeft, 
  Loader2, Edit, CheckCircle, Ban, AlertTriangle, Eye, RefreshCw, Filter, Search, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUsers, DbProfile } from '@/lib/useUsers';
import { useOrganizationHierarchy } from '@/lib/useOrganizationHierarchy';
import { useToast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';
import { rolesApi, Role } from '@/api/rolesApi';
import { ROLE_PERMISSIONS } from '@/lib/permissions';

const PREDEFINED_ROLES = [
  'Super Admin',
  'Admin',
  'Technician',
  'User',
  'SOC User',
  'SOC Technician',
  'Team Lead',
  'Department Head',
  'Approver',
  'Report Viewer'
];

const ROLE_STYLE_MAP: Record<string, { color: string; bg: string; border: string }> = {
  'Super Admin': { color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
  'SUPERADMIN': { color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
  'Admin': { color: 'text-indigo-500', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' },
  'ADMIN': { color: 'text-indigo-500', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' },
  'Technician': { color: 'text-cyan-500', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  'TECHNICIAN': { color: 'text-cyan-500', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  'User': { color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/20' },
  'Normal User': { color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/20' },
  'USER': { color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/20' },
  'SOC User': { color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  'SOC_USER': { color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  'SOC Technician': { color: 'text-sky-500', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
  'SOC_TECHNICIAN': { color: 'text-sky-500', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
  'Team Lead': { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  'TEAM_LEAD': { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  'Department Head': { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  'DEPARTMENT_HEAD': { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  'Approver': { color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  'APPROVER': { color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  'Report Viewer': { color: 'text-teal-500', bg: 'bg-teal-500/10', border: 'border-teal-500/20' },
  'REPORT_VIEWER': { color: 'text-teal-500', bg: 'bg-teal-500/10', border: 'border-teal-500/20' },
  'Agent': { color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  'Manager': { color: 'text-violet-500', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
};

export function UserManagementPage({ 
  onBack, 
  initialTab = 'users',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  
  const { profiles, loading: usersLoading, addUser, updateUser, updateUserStatus, deleteUser, refresh } = useUsers();
  const { organizations, teams, departments } = useOrganizationHierarchy();
  const { hasPermission } = useAuth();
  const toast = useToast();

  const [dbRoles, setDbRoles] = useState<Role[]>([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [newRoleTemplate, setNewRoleTemplate] = useState('Technician');
  const [isCreatingRole, setIsCreatingRole] = useState(false);

  const fetchRolesData = async () => {
    try {
      const r = await rolesApi.getRoles().catch(() => []);
      if (r && r.length > 0) setDbRoles(r);
    } catch (e) {
      console.error('Failed to load roles from API', e);
    }
  };

  useEffect(() => {
    fetchRolesData();
  }, []);

  useEffect(() => {
    const handleUsersChanged = () => {
      refresh();
    };
    window.addEventListener('compass_users_changed', handleUsersChanged);
    return () => window.removeEventListener('compass_users_changed', handleUsersChanged);
  }, [refresh]);
  
  const canCreateUser = hasPermission('user.create');
  const canUpdateUser = hasPermission('user.update');
  const canDisableUser = hasPermission('user.disable');

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [teamFilter, setTeamFilter] = useState('ALL');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [authTypeFilter, setAuthTypeFilter] = useState('ALL');
  const [userTypeFilter, setUserTypeFilter] = useState('ALL');
  
  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<DbProfile | null>(null);
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  // Form State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedRole, setSelectedRole] = useState('User');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [authMethod, setAuthMethod] = useState<'both' | 'email_password' | 'microsoft_sso'>('both');
  const [password, setPassword] = useState('ChangeMe@123');
  const [isSaving, setIsSaving] = useState(false);

  // Quick Action in Details Modal
  const [detailSelectedRole, setDetailSelectedRole] = useState('');
  const [detailSelectedTeam, setDetailSelectedTeam] = useState('');
  const [detailSelectedDept, setDetailSelectedDept] = useState('');
  const [isUpdatingDetail, setIsUpdatingDetail] = useState(false);

  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedRoleForPerms, setSelectedRoleForPerms] = useState<any>(null);
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);

  const handleOpenCreateModal = () => {
    setEditingUserId(null);
    setFirstName('');
    setLastName('');
    setDisplayName('');
    setEmail('');
    setEmployeeId('');
    setPhone('');
    setSelectedOrgId(organizations[0]?.id || '');
    setSelectedDeptId('');
    setSelectedTeamId('');
    setSelectedRole('User');
    setSelectedStatus('Active');
    setAuthMethod('both');
    setPassword('ChangeMe@123');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (user: DbProfile) => {
    setEditingUserId(user.id);
    const names = (user.full_name || '').split(' ');
    setFirstName(names[0] || '');
    setLastName(names.slice(1).join(' ') || '');
    setDisplayName(user.full_name || '');
    setEmail(user.email || '');
    setEmployeeId(user.employee_id || '');
    setPhone(user.mobile || user.phone || '');
    setSelectedOrgId(user.organization_id || organizations[0]?.id || '');
    setSelectedDeptId(user.department_id || '');
    setSelectedTeamId(user.team_id || '');
    setSelectedRole(user.role || 'User');
    setSelectedStatus(user.status || user.account_status || 'Active');
    setAuthMethod((user.auth_method as any) || 'both');
    setPassword('');
    setIsModalOpen(true);
  };

  const handleOpenDetailsModal = async (user: DbProfile) => {
    setViewingUser(user);
    setDetailSelectedRole(user.role || 'User');
    setDetailSelectedTeam(user.team_id || '');
    setDetailSelectedDept(user.department_id || '');
    setUserTeams([]);
    setIsDetailsModalOpen(true);

    // Fetch user team memberships
    try {
      const res = await fetch(`/api/teams/user/${user.id}`);
      if (res.ok) {
        const tms = await res.json();
        setUserTeams(tms);
      }
    } catch {
      // Fallback
    }
  };

  const handleSaveUser = async () => {
    const fullNameCombined = displayName.trim() || `${firstName.trim()} ${lastName.trim()}`.trim() || email.split('@')[0];
    
    if (!fullNameCombined || !email || !selectedOrgId) {
      toast.push('Please fill in required fields: Name, Email, and Organization.');
      return;
    }

    try {
      setIsSaving(true);
      if (editingUserId) {
        await updateUser(editingUserId, { 
          full_name: fullNameCombined,
          role: selectedRole,
          organization_id: selectedOrgId,
          department_id: selectedDeptId || null,
          team_id: selectedTeamId || null,
          status: selectedStatus,
          account_status: selectedStatus,
          employee_id: employeeId || null,
          mobile: phone || null,
          auth_method: authMethod
        });
        toast.push('User updated successfully!');
      } else {
        await addUser(
          selectedOrgId,
          email,
          fullNameCombined,
          selectedRole,
          {
            password: authMethod !== 'microsoft_sso' ? (password || 'ChangeMe@123') : undefined,
            username: email.split('@')[0],
            employee_id: employeeId || undefined,
            mobile: phone || undefined,
            department_id: selectedDeptId || undefined,
            team_id: selectedTeamId || undefined,
            status: selectedStatus,
            auth_method: authMethod
          }
        );
        toast.push('User provisioned successfully!');
      }
      setIsModalOpen(false);
      refresh();
    } catch (e: any) {
      console.error('Failed to save user', e);
      toast.push(e?.message || 'Failed to save user.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (user: DbProfile, newStatus: string) => {
    if (!canDisableUser) {
      toast.push('You do not have permission to change user account status.');
      return;
    }
    try {
      await updateUserStatus(user.id, newStatus);
      toast.push(`User marked as ${newStatus}`);
      refresh();
      if (viewingUser && viewingUser.id === user.id) {
        setViewingUser({ ...viewingUser, status: newStatus, account_status: newStatus });
      }
    } catch (e: any) {
      toast.push(e.message || 'Failed to update user status');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(id);
        toast.push('User deleted successfully.');
        refresh();
      } catch (e: any) {
        console.error(e);
        toast.push(e.message || 'Failed to delete user.');
      }
    }
  };

  // Quick Action in Details Modal: Update Role
  const handleDetailUpdateRole = async (newRole: string) => {
    if (!viewingUser) return;
    try {
      setIsUpdatingDetail(true);
      await updateUser(viewingUser.id, { role: newRole });
      setDetailSelectedRole(newRole);
      setViewingUser({ ...viewingUser, role: newRole });
      toast.push(`Role updated to ${newRole}`);
      refresh();
    } catch (e: any) {
      toast.push(`Failed to update role: ${e.message}`);
    } finally {
      setIsUpdatingDetail(false);
    }
  };

  // Quick Action in Details Modal: Update Team
  const handleDetailUpdateTeam = async (newTeamId: string) => {
    if (!viewingUser) return;
    try {
      setIsUpdatingDetail(true);
      await updateUser(viewingUser.id, { team_id: newTeamId || null });
      setDetailSelectedTeam(newTeamId);
      setViewingUser({ ...viewingUser, team_id: newTeamId || null });
      toast.push('Team assignment updated');
      refresh();
    } catch (e: any) {
      toast.push(`Failed to update team: ${e.message}`);
    } finally {
      setIsUpdatingDetail(false);
    }
  };

  // Quick Action in Details Modal: Update Department
  const handleDetailUpdateDept = async (newDeptId: string) => {
    if (!viewingUser) return;
    try {
      setIsUpdatingDetail(true);
      await updateUser(viewingUser.id, { department_id: newDeptId || null });
      setDetailSelectedDept(newDeptId);
      setViewingUser({ ...viewingUser, department_id: newDeptId || null });
      toast.push('Department assignment updated');
      refresh();
    } catch (e: any) {
      toast.push(`Failed to update department: ${e.message}`);
    } finally {
      setIsUpdatingDetail(false);
    }
  };

  const filteredTeams = useMemo(() => {
    if (!selectedOrgId) return teams;
    return teams.filter(t => t.organization_id === selectedOrgId);
  }, [teams, selectedOrgId]);

  const filteredDepartments = useMemo(() => {
    if (!selectedOrgId) return departments;
    return departments.filter(d => d.organization_id === selectedOrgId);
  }, [departments, selectedOrgId]);

  // Combined Filter Logic (Req 25)
  const filteredUsers = useMemo(() => {
    return profiles.filter(u => {
      const q = search.toLowerCase();
      const matchesSearch = 
        !search ||
        (u.full_name && u.full_name.toLowerCase().includes(q)) || 
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.employee_id && u.employee_id.toLowerCase().includes(q)) ||
        (u.role && u.role.toLowerCase().includes(q));

      if (!matchesSearch) return false;

      // Role Filter
      if (roleFilter !== 'ALL') {
        const rf = roleFilter.toLowerCase();
        const ur = (u.role || '').toLowerCase();
        if (rf === 'user' || rf === 'normal user') {
          if (ur !== 'user' && ur !== 'normal user') return false;
        } else if (ur !== rf) {
          return false;
        }
      }

      // Team Filter
      if (teamFilter !== 'ALL') {
        if (u.team_id !== teamFilter) return false;
      }

      // Department Filter
      if (deptFilter !== 'ALL') {
        if (u.department_id !== deptFilter && u.department !== deptFilter) return false;
      }

      // Status Filter
      if (statusFilter !== 'ALL') {
        const s = (u.status || u.account_status || 'Active').toUpperCase();
        if (!s.includes(statusFilter.toUpperCase())) return false;
      }

      // SSO vs Local Filter
      if (authTypeFilter === 'SSO') {
        const isSso = u.auth_method === 'microsoft_sso' || !!u.sso_uid || !!u.azure_object_id || !!u.sso_provider;
        if (!isSso) return false;
      } else if (authTypeFilter === 'LOCAL') {
        const isSsoOnly = u.auth_method === 'microsoft_sso';
        if (isSsoOnly) return false;
      }

      // User Type Filter (Member vs Guest)
      if (userTypeFilter !== 'ALL') {
        const ut = ((u as any).user_type || 'Member').toUpperCase();
        if (ut !== userTypeFilter) return false;
      }

      return true;
    });
  }, [profiles, search, roleFilter, teamFilter, deptFilter, statusFilter, authTypeFilter, userTypeFilter]);

  return (
    <div className="flex h-full flex-col relative">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card/60 px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight">User Management & RBAC</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Enterprise administration, Predefined RBAC Roles, Teams & SSO User Provisioning.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => refresh()}
            className="flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-muted-foreground hover:text-foreground transition"
            title="Refresh Users"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          {canCreateUser && (
            <button 
              onClick={handleOpenCreateModal}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Create User
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-muted/10 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto w-full max-w-7xl space-y-6">
          
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
            {[
              { id: 'users', label: 'All Users', icon: Users },
              { id: 'roles', label: 'Roles & Permissions', icon: Shield },
              { id: 'azure', label: 'SSO Directory', icon: Download },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                  activeTab === tab.id ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'users' && (
            <div className="space-y-4">
              {/* Search & Filters (Req 25) */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-card p-4 rounded-xl border border-border shadow-sm">
                <div className="relative max-w-sm w-full">
                  <input 
                    type="text" 
                    placeholder="Search by name, email, employee ID..." 
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background pl-10 pr-4 py-2 text-sm outline-none transition focus:border-primary"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
                
                <div className="flex flex-wrap items-center gap-2.5">
                  <div className="flex items-center gap-1.5">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    {/* Role Filter */}
                    <select
                      value={roleFilter}
                      onChange={e => setRoleFilter(e.target.value)}
                      className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs font-medium outline-none"
                    >
                      <option value="ALL">All Roles</option>
                      {PREDEFINED_ROLES.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  {/* Team Filter */}
                  <select
                    value={teamFilter}
                    onChange={e => setTeamFilter(e.target.value)}
                    className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs font-medium outline-none max-w-[140px] truncate"
                  >
                    <option value="ALL">All Teams</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>

                  {/* Department Filter */}
                  <select
                    value={deptFilter}
                    onChange={e => setDeptFilter(e.target.value)}
                    className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs font-medium outline-none max-w-[140px] truncate"
                  >
                    <option value="ALL">All Depts</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs font-medium outline-none"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="ACTIVE">Active</option>
                    <option value="DISABLED">Disabled</option>
                    <option value="BLOCKED">Blocked</option>
                    <option value="SUSPENDED">Suspended</option>
                    <option value="PENDING">Pending</option>
                  </select>

                  {/* SSO vs Local User Filter */}
                  <select
                    value={authTypeFilter}
                    onChange={e => setAuthTypeFilter(e.target.value)}
                    className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs font-medium outline-none"
                  >
                    <option value="ALL">All Auth Types</option>
                    <option value="SSO">SSO Users Only</option>
                    <option value="LOCAL">Local / Password</option>
                  </select>

                  {/* Member vs Guest User Type Filter */}
                  <select
                    value={userTypeFilter}
                    onChange={e => setUserTypeFilter(e.target.value)}
                    className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs font-medium outline-none"
                  >
                    <option value="ALL">All User Types</option>
                    <option value="MEMBER">Members Only</option>
                    <option value="GUEST">Guest Users</option>
                  </select>
                </div>
              </div>

              {/* Users Table */}
              <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                    <tr>
                      <th className="px-5 py-3.5 font-semibold uppercase tracking-wider text-[11px]">User Name & Email</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">User Type</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">SSO Provider</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">SSO User ID</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Role</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Department</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Team</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Status & Sync</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Last Login</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Created Date</th>
                      <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Auth Type</th>
                      <th className="px-5 py-3.5 text-right font-semibold uppercase tracking-wider text-[11px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {usersLoading ? (
                      <tr>
                        <td colSpan={12} className="px-6 py-12 text-center text-muted-foreground">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                          Loading users...
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="px-6 py-12 text-center text-muted-foreground">No users found matching your filters.</td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => {
                        const dept = departments.find(d => d.id === user.department_id) || (user.department ? { name: user.department } : null);
                        const team = teams.find(t => t.id === user.team_id);
                        const statusStr = (user.status || user.account_status || 'Active').toUpperCase();
                        const ssoProvider = user.sso_provider || (user.azure_object_id || user.auth_method === 'microsoft_sso' ? 'Azure AD / Entra' : '—');
                        const ssoUid = user.sso_uid || user.azure_object_id || '—';
                        const styling = ROLE_STYLE_MAP[user.role] || { color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/20' };
                        const isGuest = ((user as any).user_type || '').toLowerCase() === 'guest';
                        const syncStatus = (user as any).sso_sync_status || 'SYNCED';

                        return (
                          <tr key={user.id} className="transition hover:bg-muted/30">
                            {/* Name & Email */}
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs shrink-0">
                                  {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                                </div>
                                <div className="overflow-hidden">
                                  <div className="font-semibold text-foreground truncate">{user.full_name || 'Unnamed User'}</div>
                                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                  {user.employee_id && (
                                    <div className="text-[10px] text-muted-foreground font-mono">ID: {user.employee_id}</div>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* User Type (Member vs Guest) */}
                            <td className="px-4 py-3.5">
                              {isGuest ? (
                                <span className="inline-flex items-center rounded-full bg-purple-500/10 px-2 py-0.5 text-[11px] font-semibold text-purple-400 border border-purple-500/20">
                                  Guest
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-slate-500/10 px-2 py-0.5 text-[11px] font-medium text-slate-400 border border-slate-500/20">
                                  Member
                                </span>
                              )}
                            </td>

                            {/* SSO Provider */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs font-medium text-foreground">{ssoProvider}</span>
                            </td>

                            {/* SSO User ID */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs font-mono text-muted-foreground truncate max-w-[120px] block" title={ssoUid}>
                                {ssoUid !== '—' ? `${ssoUid.substring(0, 8)}...` : '—'}
                              </span>
                            </td>

                            {/* Role */}
                            <td className="px-4 py-3.5">
                              <span className={cn(
                                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border",
                                styling.bg, styling.color, styling.border
                              )}>
                                {user.role === 'Agent' ? 'Technician' : user.role}
                              </span>
                            </td>

                            {/* Department */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs font-medium text-foreground">{dept?.name || '—'}</span>
                            </td>

                            {/* Team */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs font-medium text-foreground">{team?.name || '—'}</span>
                            </td>

                            {/* Status & Sync */}
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col gap-1">
                                <span className={cn(
                                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold w-fit",
                                  statusStr.includes('ACT') ? "bg-emerald-500/10 text-emerald-500" :
                                  statusStr.includes('BLOCK') ? "bg-rose-600/10 text-rose-600" :
                                  statusStr.includes('DISAB') ? "bg-rose-500/10 text-rose-500" :
                                  statusStr.includes('SUSP') ? "bg-amber-500/10 text-amber-500" :
                                  "bg-blue-500/10 text-blue-500"
                                )}>
                                  <span className={cn(
                                    "h-1.5 w-1.5 rounded-full",
                                    statusStr.includes('ACT') ? "bg-emerald-500" :
                                    statusStr.includes('BLOCK') ? "bg-rose-600" :
                                    statusStr.includes('DISAB') ? "bg-rose-500" :
                                    statusStr.includes('SUSP') ? "bg-amber-500" : "bg-blue-500"
                                  )}></span>
                                  {user.status || 'Active'}
                                </span>
                                {(user as any).last_sso_sync_at && (
                                  <span className="text-[10px] text-muted-foreground flex items-center gap-1" title={new Date((user as any).last_sso_sync_at).toLocaleString()}>
                                    <span className="h-1 w-1 rounded-full bg-emerald-500" />
                                    {syncStatus}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Last Login */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs text-muted-foreground">
                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                              </span>
                            </td>

                            {/* Created Date */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs text-muted-foreground">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                              </span>
                            </td>

                            {/* Authentication Type */}
                            <td className="px-4 py-3.5">
                              <span className="text-xs text-muted-foreground font-medium">
                                {user.auth_method === 'microsoft_sso' ? 'SSO (Entra ID)' :
                                 user.auth_method === 'email_password' ? 'Email & Password' : 'Both (SSO + PW)'}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-3.5 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <button 
                                  onClick={() => handleOpenDetailsModal(user)} 
                                  title="View User Details"
                                  className="p-1.5 text-muted-foreground hover:text-foreground transition bg-card rounded-lg border border-border hover:border-primary/40 shadow-sm"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                
                                {canUpdateUser && (
                                  <button 
                                    onClick={() => handleOpenEditModal(user)} 
                                    title="Edit User"
                                    className="p-1.5 text-muted-foreground hover:text-primary transition bg-card rounded-lg border border-border hover:border-primary/40 shadow-sm"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                )}

                                {canDisableUser && (
                                  <>
                                    {statusStr.includes('ACT') ? (
                                      <button 
                                        onClick={() => handleToggleStatus(user, 'Disabled')}
                                        title="Disable User"
                                        className="p-1.5 text-muted-foreground hover:text-rose-500 transition bg-card rounded-lg border border-border hover:border-rose-500/40 shadow-sm"
                                      >
                                        <Ban className="h-4 w-4" />
                                      </button>
                                    ) : (
                                      <button 
                                        onClick={() => handleToggleStatus(user, 'Active')}
                                        title="Activate User"
                                        className="p-1.5 text-muted-foreground hover:text-emerald-500 transition bg-card rounded-lg border border-border hover:border-emerald-500/40 shadow-sm"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                    )}
                                  </>
                                )}

                                {canUpdateUser && (
                                  <button 
                                    onClick={() => handleDelete(user.id)} 
                                    title="Delete User"
                                    className="p-1.5 text-muted-foreground hover:text-rose-500 transition bg-card rounded-lg border border-border hover:border-rose-500/40 shadow-sm"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Predefined RBAC Roles & Permissions</h2>
                  <p className="text-sm text-muted-foreground mt-1">10 Predefined System Roles with locked permissions and custom role provisioning.</p>
                </div>
                <button onClick={() => setIsCreateRoleModalOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90">
                  <Plus className="h-4 w-4" /> Create Custom Role
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {PREDEFINED_ROLES.map((roleName) => {
                  const styling = ROLE_STYLE_MAP[roleName] || { color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' };
                  const usersCount = profiles.filter(p => (p.role || '').toLowerCase() === roleName.toLowerCase() || (roleName === 'User' && p.role === 'Normal User')).length;
                  const perms = ROLE_PERMISSIONS[roleName] || ROLE_PERMISSIONS[roleName.toLowerCase()] || [];

                  return (
                    <div key={roleName} className="flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md hover:border-primary/30">
                      <div>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg", styling.bg, styling.color)}>
                              <Shield className="h-4 w-4" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground flex items-center gap-2">
                                {roleName}
                                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Predefined</span>
                              </h3>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4 line-clamp-2">
                          {roleName === 'Super Admin' ? 'Full system administration across all tenants and resources.' :
                           roleName === 'Admin' ? 'Enterprise organization, user management, and team administration.' :
                           roleName === 'Technician' ? 'IT technician & detection engineering: rule development, testing, and ticket resolution.' :
                           roleName === 'SOC User' ? 'Security Operations analyst: alerts review, investigations, and Sentinel ticket triage.' :
                           roleName === 'SOC Technician' ? 'SOC technical lead: threat remediation, rule verification, and playbook execution.' :
                           roleName === 'Team Lead' ? 'Functional team management, ticket queue distribution, and SLA oversight.' :
                           roleName === 'Department Head' ? 'Departmental scope management, escalations, and executive metrics.' :
                           roleName === 'Approver' ? 'Authority to review, approve, or reject operational change requests.' :
                           roleName === 'Report Viewer' ? 'Read-only access to organizational analytics and SLA compliance reports.' :
                           'Standard requester: submit tickets, track workflow, and receive updates.'}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                          <Users className="h-4 w-4" /> {usersCount} Users
                        </div>
                        <button 
                          onClick={() => {
                            setSelectedRoleForPerms({ name: roleName, default: true, permissions: perms.map(p => ({ permission_name: p })) });
                            setIsPermissionsModalOpen(true);
                          }}
                          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" /> View Permissions ({perms.length})
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Custom Roles from DB */}
                {dbRoles.filter(r => !PREDEFINED_ROLES.map(p => p.toLowerCase()).includes((r.role_name || '').toLowerCase())).map((role) => {
                  return (
                    <div key={role.id} className="flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md hover:border-primary/30">
                      <div>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                              <Shield className="h-4 w-4" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground flex items-center gap-2">
                                {role.role_name}
                                <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">Custom</span>
                              </h3>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{role.description || 'Custom organizational role.'}</p>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                          <Users className="h-4 w-4" /> {profiles.filter(p => p.role === role.role_name).length} Users
                        </div>
                        <button 
                          onClick={() => {
                            setSelectedRoleForPerms({ name: role.role_name, default: false, permissions: role.permissions || [] });
                            setIsPermissionsModalOpen(true);
                          }}
                          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" /> View Permissions
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'azure' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">SSO User Provisioning Directory</h2>
                  <p className="text-sm text-muted-foreground mt-1">Users synchronized or provisioned via SSO (Microsoft Entra ID, SAML, OAuth).</p>
                </div>
                <button onClick={() => { refresh(); toast.push("SSO directory refreshed."); }} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90">
                  <RefreshCw className="h-4 w-4" /> Sync Now
                </button>
              </div>

              <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">User Profile</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">SSO Provider</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">External User ID</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Assigned Role</th>
                      <th className="px-6 py-3 font-semibold uppercase tracking-wider text-[11px]">Status</th>
                      <th className="px-6 py-3 text-right font-semibold uppercase tracking-wider text-[11px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredUsers.filter(u => u.azure_object_id || u.auth_method === 'microsoft_sso' || u.sso_uid || u.sso_provider).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">No SSO provisioned user accounts recorded yet.</td>
                      </tr>
                    ) : (
                      filteredUsers.filter(u => u.azure_object_id || u.auth_method === 'microsoft_sso' || u.sso_uid || u.sso_provider).map((user) => (
                        <tr key={user.id} className="transition hover:bg-muted/30">
                          <td className="px-6 py-4">
                            <div className="font-medium text-foreground">{user.full_name}</div>
                            <div className="text-xs text-muted-foreground">{user.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs font-medium text-foreground">{user.sso_provider || 'Microsoft Entra ID'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs font-mono text-muted-foreground">{user.sso_uid || user.azure_object_id || '—'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primary">
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-500">
                              {user.status || 'Active'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button 
                              onClick={() => handleOpenDetailsModal(user)}
                              className="text-xs font-medium text-primary hover:underline mr-3"
                            >
                              View Details
                            </button>
                            <button 
                              onClick={() => handleOpenEditModal(user)}
                              className="text-xs font-medium text-muted-foreground hover:underline"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CREATE / EDIT USER MODAL */}
      {isModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-border p-6 flex-shrink-0 bg-card">
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground">
                  {editingUserId ? 'Edit User' : 'Create Application User'}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter user details, assign department, team, role, and authentication method.
                </p>
              </div>
              <button 
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition -mr-2 -mt-2"
                title="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Modal Scrollable Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Names */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">First Name *</label>
                  <input 
                    value={firstName} 
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="e.g., Alex"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Last Name</label>
                  <input 
                    value={lastName} 
                    onChange={e => setLastName(e.target.value)}
                    placeholder="e.g., Morgan"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
              </div>

              {/* Display Name & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display Name</label>
                  <input 
                    value={displayName} 
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder={firstName ? `${firstName} ${lastName}`.trim() : 'e.g., Alex Morgan'}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email Address *</label>
                  <input 
                    type="email"
                    value={email} 
                    disabled={!!editingUserId}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="alex.morgan@company.com"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary disabled:opacity-50" 
                  />
                </div>
              </div>

              {/* Employee ID & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Employee ID</label>
                  <input 
                    value={employeeId} 
                    onChange={e => setEmployeeId(e.target.value)}
                    placeholder="e.g., EMP-1042"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone / Mobile</label>
                  <input 
                    value={phone} 
                    onChange={e => setPhone(e.target.value)}
                    placeholder="e.g., +1 555-0199"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
              </div>

              {/* Organization & Department */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organization *</label>
                  <select 
                    value={selectedOrgId} 
                    onChange={e => {
                      setSelectedOrgId(e.target.value);
                      setSelectedDeptId('');
                      setSelectedTeamId('');
                    }}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="">Select Organization...</option>
                    {organizations.map(o => (
                      <option key={o.id} value={o.id}>{o.organization_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Department</label>
                  <select 
                    value={selectedDeptId} 
                    onChange={e => setSelectedDeptId(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="">No Department</option>
                    {filteredDepartments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Team & Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Functional Team</label>
                  <select 
                    value={selectedTeamId} 
                    onChange={e => setSelectedTeamId(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="">No Team Assigned</option>
                    {filteredTeams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role *</label>
                  <select 
                    value={selectedRole} 
                    onChange={e => setSelectedRole(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-medium"
                  >
                    <optgroup label="Predefined Roles">
                      {PREDEFINED_ROLES.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </optgroup>
                    {dbRoles.filter(r => !PREDEFINED_ROLES.map(p => p.toLowerCase()).includes((r.role_name || '').toLowerCase())).length > 0 && (
                      <optgroup label="Custom Roles">
                        {dbRoles.filter(r => !PREDEFINED_ROLES.map(p => p.toLowerCase()).includes((r.role_name || '').toLowerCase())).map(r => (
                          <option key={r.id} value={r.role_name}>{r.role_name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              </div>

              {/* Status & Auth Method */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status *</label>
                  <select 
                    value={selectedStatus} 
                    onChange={e => setSelectedStatus(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="Active">ACTIVE</option>
                    <option value="Disabled">DISABLED</option>
                    <option value="Blocked">BLOCKED</option>
                    <option value="Suspended">SUSPENDED</option>
                    <option value="Pending">PENDING</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Authentication Method *</label>
                  <select
                    value={authMethod}
                    onChange={e => setAuthMethod(e.target.value as any)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="both">Both (SSO + Password)</option>
                    <option value="microsoft_sso">Microsoft SSO Only</option>
                    <option value="email_password">Email & Password Only</option>
                  </select>
                </div>
              </div>

              {/* Temporary Password */}
              {!editingUserId && authMethod !== 'microsoft_sso' && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Temporary Password *</label>
                  <input 
                    type="text" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)}
                    placeholder="ChangeMe@123"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">The user will be required to update their password upon initial login.</p>
                </div>
              )}
            </div>

            {/* Modal Fixed Footer */}
            <div className="flex justify-end gap-3 border-t border-border px-6 py-4 bg-muted/20 flex-shrink-0">
              <button 
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleSaveUser}
                disabled={isSaving || !email || !selectedOrgId || (!displayName && !firstName)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50 shadow-sm"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editingUserId ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* VIEW USER DETAILS MODAL (Req 26: Full Details + Admin Actions) */}
      {isDetailsModalOpen && viewingUser && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsDetailsModalOpen(false);
          }}
        >
          <div className="w-full max-w-xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-6 flex-shrink-0 bg-card">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-base">
                  {(viewingUser.full_name || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold">{viewingUser.full_name}</h2>
                  <p className="text-xs text-muted-foreground">{viewingUser.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                  (viewingUser.status || '').toUpperCase().includes('ACT') ? "bg-emerald-500/10 text-emerald-500" :
                  (viewingUser.status || '').toUpperCase().includes('BLOCK') ? "bg-rose-600/10 text-rose-600" :
                  "bg-rose-500/10 text-rose-500"
                )}>
                  {viewingUser.status || 'Active'}
                </span>
                <button 
                  type="button"
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4 text-sm">
              {/* Profile Overview */}
              <div className="grid grid-cols-2 gap-3 text-xs bg-muted/20 p-3 rounded-xl border border-border">
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase font-semibold">User ID</span>
                  <span className="font-mono text-foreground truncate block">{viewingUser.id}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Tenant / Organization</span>
                  <span className="font-medium text-foreground truncate block">
                    {organizations.find(o => o.id === viewingUser.organization_id)?.organization_name || 'Rezilyens Corp'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase font-semibold">SSO Provider</span>
                  <span className="font-medium text-foreground">
                    {viewingUser.sso_provider || (viewingUser.azure_object_id || viewingUser.auth_method === 'microsoft_sso' ? 'Microsoft Entra ID' : 'None (Local)')}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase font-semibold">External User ID</span>
                  <span className="font-mono text-foreground truncate block">
                    {viewingUser.sso_uid || viewingUser.azure_object_id || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Created Date</span>
                  <span className="text-foreground">
                    {viewingUser.created_at ? new Date(viewingUser.created_at).toLocaleString() : '—'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Last Login</span>
                  <span className="text-foreground">
                    {viewingUser.last_login ? new Date(viewingUser.last_login).toLocaleString() : 'Never'}
                  </span>
                </div>
              </div>

              {/* Administrative Actions: Role, Department, Team (Req 26) */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Administrative Assignments</h4>
                
                {/* Role Assignment */}
                <div className="flex items-center justify-between gap-4 p-3 bg-card border border-border rounded-xl">
                  <div>
                    <div className="font-semibold text-xs">Role</div>
                    <div className="text-[11px] text-muted-foreground">Controls platform permissions</div>
                  </div>
                  <select
                    value={detailSelectedRole}
                    onChange={e => handleDetailUpdateRole(e.target.value)}
                    disabled={isUpdatingDetail}
                    className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs font-semibold outline-none focus:border-primary"
                  >
                    {PREDEFINED_ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {/* Department Assignment */}
                <div className="flex items-center justify-between gap-4 p-3 bg-card border border-border rounded-xl">
                  <div>
                    <div className="font-semibold text-xs">Department</div>
                    <div className="text-[11px] text-muted-foreground">Organizational unit</div>
                  </div>
                  <select
                    value={detailSelectedDept}
                    onChange={e => handleDetailUpdateDept(e.target.value)}
                    disabled={isUpdatingDetail}
                    className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs font-medium outline-none focus:border-primary max-w-[180px] truncate"
                  >
                    <option value="">No Department</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                {/* Primary Team Assignment */}
                <div className="flex items-center justify-between gap-4 p-3 bg-card border border-border rounded-xl">
                  <div>
                    <div className="font-semibold text-xs">Primary Team</div>
                    <div className="text-[11px] text-muted-foreground">Operational functional team</div>
                  </div>
                  <select
                    value={detailSelectedTeam}
                    onChange={e => handleDetailUpdateTeam(e.target.value)}
                    disabled={isUpdatingDetail}
                    className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs font-medium outline-none focus:border-primary max-w-[180px] truncate"
                  >
                    <option value="">No Team Assigned</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {/* Teams (Primary & Secondary Memberships) */}
                {userTeams.length > 0 && (
                  <div className="p-3 bg-card border border-border rounded-xl space-y-2">
                    <div className="text-xs font-semibold">Assigned Teams</div>
                    <div className="flex flex-wrap gap-1.5">
                      {userTeams.map(tm => (
                        <span key={tm.id} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-foreground">
                          <Users className="h-3 w-3 text-primary" />
                          {tm.teams?.name || tm.team_id}
                          {tm.is_primary && <span className="text-[10px] text-emerald-500 font-bold ml-1">(Primary)</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Actions (Req 26: Activate, Deactivate, Block) */}
              <div className="space-y-2 pt-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Account Status Control</h4>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(viewingUser, 'Active')}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border text-xs font-semibold transition",
                      (viewingUser.status || '').toUpperCase().includes('ACT') 
                        ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/40" 
                        : "hover:bg-emerald-500/10 hover:text-emerald-500 border-border"
                    )}
                  >
                    <CheckCircle className="h-3.5 w-3.5" /> Activate
                  </button>

                  <button
                    type="button"
                    onClick={() => handleToggleStatus(viewingUser, 'Disabled')}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border text-xs font-semibold transition",
                      (viewingUser.status || '').toUpperCase().includes('DISAB') 
                        ? "bg-rose-500/20 text-rose-500 border-rose-500/40" 
                        : "hover:bg-rose-500/10 hover:text-rose-500 border-border"
                    )}
                  >
                    <Ban className="h-3.5 w-3.5" /> Deactivate
                  </button>

                  <button
                    type="button"
                    onClick={() => handleToggleStatus(viewingUser, 'Blocked')}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border text-xs font-semibold transition",
                      (viewingUser.status || '').toUpperCase().includes('BLOCK') 
                        ? "bg-rose-700/20 text-rose-700 border-rose-700/40" 
                        : "hover:bg-rose-700/10 hover:text-rose-700 border-border"
                    )}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" /> Block
                  </button>
                </div>
              </div>

              {/* Effective Role Permissions (Req 26) */}
              <div className="pt-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Effective Role Permissions</h4>
                <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto p-2 bg-muted/20 rounded-lg border border-border">
                  {(ROLE_PERMISSIONS[viewingUser.role] || ROLE_PERMISSIONS[viewingUser.role?.toLowerCase()] || ['ticket.view']).map((perm) => (
                    <span key={perm} className="rounded bg-card border border-border px-2 py-0.5 text-[10px] font-mono text-primary font-medium">
                      {perm}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border flex justify-end bg-muted/20 flex-shrink-0">
              <button 
                onClick={() => setIsDetailsModalOpen(false)}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PERMISSIONS VIEW MODAL */}
      {isPermissionsModalOpen && selectedRoleForPerms && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsPermissionsModalOpen(false);
          }}
        >
          <div className="w-full max-w-2xl rounded-2xl border border-border bg-card shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0 bg-card">
              <div>
                <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                  Role Permissions: {selectedRoleForPerms.name}
                  {selectedRoleForPerms.default && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">Predefined System</span>}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Granted security authorizations and functional scope for <strong>{selectedRoleForPerms.name}</strong>.</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsPermissionsModalOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition -mr-2 -mt-2"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Enterprise Permission Matrix</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {selectedRoleForPerms.permissions && selectedRoleForPerms.permissions.length > 0 ? (
                    selectedRoleForPerms.permissions.map((p: any) => (
                      <div key={p.id || p.permission_name || p} className="p-2 bg-card rounded border border-border font-mono text-primary font-semibold flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> {p.permission_name || p}
                      </div>
                    ))
                  ) : (
                    <div className="p-3 bg-rose-500/10 text-rose-500 rounded-lg font-mono font-semibold col-span-2">
                      * (Full system access across all platform capabilities)
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end bg-muted/20 flex-shrink-0">
              <button 
                onClick={() => setIsPermissionsModalOpen(false)}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CREATE CUSTOM ROLE MODAL */}
      {isCreateRoleModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsCreateRoleModalOpen(false);
          }}
        >
          <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="flex items-start justify-between border-b border-border p-6 flex-shrink-0 bg-card">
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground">Create Custom Role</h2>
                <p className="text-xs text-muted-foreground mt-1">Define an organization-specific custom role.</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsCreateRoleModalOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition -mr-2 -mt-2"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role Name *</label>
                <input 
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., Senior Support Specialist"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
                <textarea 
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                  placeholder="Responsibilities and access scope..."
                  rows={3}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary resize-none" 
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Base Template</label>
                <select 
                  value={newRoleTemplate}
                  onChange={(e) => setNewRoleTemplate(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  <option value="Technician">Clone Technician Permissions</option>
                  <option value="SOC User">Clone SOC User Permissions</option>
                  <option value="Team Lead">Clone Team Lead Permissions</option>
                  <option value="Report Viewer">Clone Report Viewer Permissions</option>
                  <option value="User">Clone User Permissions</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border px-6 py-4 bg-muted/20 flex-shrink-0">
              <button 
                type="button"
                onClick={() => setIsCreateRoleModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                disabled={isCreatingRole}
                onClick={async () => {
                  if (!newRoleName.trim()) {
                    toast.push("Please provide a role name.");
                    return;
                  }
                  try {
                    setIsCreatingRole(true);
                    await rolesApi.createRole({
                      role_name: newRoleName.trim(),
                      description: newRoleDesc.trim()
                    });
                    toast.push("Custom role created successfully.");
                    setIsCreateRoleModalOpen(false);
                    setNewRoleName('');
                    setNewRoleDesc('');
                    await fetchRolesData();
                  } catch (e: any) {
                    toast.push(`Failed to create role: ${e.message}`);
                  } finally {
                    setIsCreatingRole(false);
                  }
                }}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
              >
                {isCreatingRole && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Role
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
